/**
 * Column manager dropdown for the Deliveries table.
 *
 * Mirrors the Schedule Lines "Manage Columns" dropdown: a Columns button that
 * opens a panel where columns can be dragged to reorder and toggled to
 * show/hide. Closes on outside click. Runtime-gated columns (passed via
 * ``availableIds``) are omitted from the list so users can't toggle a column
 * that the current context can never render.
 */
import { useEffect, useRef, useState } from 'react';
import { Columns, Eye, EyeOff, GripVertical } from 'lucide-react';

import type { DeliveryColumnConfig } from './deliveryColumnConfig';

interface DeliveryColumnsDropdownProps {
  columns: DeliveryColumnConfig[];
  /** Ids currently renderable in this context; others are hidden from the list. */
  availableIds: Set<string>;
  onToggle: (id: string) => void;
  onReset: () => void;
  onSelectAll: () => void;
  /** Move the column at ``from`` (full-array index) to ``to`` (full-array index). */
  onReorder: (from: number, to: number) => void;
}

/**
 * Renders the Columns button + manage-columns dropdown.
 *
 * @param columns - Full ordered column config (drag indices map to this array).
 * @param availableIds - Ids renderable in the current context.
 * @returns The Columns toolbar control.
 */
export default function DeliveryColumnsDropdown({
  columns,
  availableIds,
  onToggle,
  onReset,
  onSelectAll,
  onReorder,
}: DeliveryColumnsDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 ${open ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
      >
        <Columns className="h-3 w-3" />Columns
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-border rounded-lg shadow-lg z-50 max-h-[400px] overflow-y-auto">
          <div className="p-2 border-b flex items-center justify-between">
            <span className="text-sm font-medium">Manage Columns</span>
            <div className="flex items-center gap-2">
              <button onClick={onSelectAll} className="text-xs text-primary hover:underline">Select All</button>
              <span className="text-xs text-muted-foreground">|</span>
              <button onClick={onReset} className="text-xs text-primary hover:underline">Reset</button>
            </div>
          </div>
          <div className="px-2 py-1 text-xs text-muted-foreground border-b bg-muted/30">Drag to reorder · Click to show/hide</div>
          <div className="p-2 space-y-1">
            {columns.map((col, idx) => {
              if (!availableIds.has(col.id)) return null;
              return (
                <div
                  key={col.id}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', idx.toString()); e.currentTarget.classList.add('opacity-50'); }}
                  onDragEnd={(e) => e.currentTarget.classList.remove('opacity-50')}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-primary/10'); }}
                  onDragLeave={(e) => e.currentTarget.classList.remove('bg-primary/10')}
                  onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('bg-primary/10'); const di = parseInt(e.dataTransfer.getData('text/plain'), 10); if (di !== idx) onReorder(di, idx); }}
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 rounded cursor-move group"
                >
                  <GripVertical className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground flex-shrink-0" />
                  <input
                    type="checkbox"
                    checked={col.visible}
                    onChange={() => onToggle(col.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                  />
                  <span className="text-sm flex-1">{col.label}</span>
                  {col.visible ? <Eye className="h-3 w-3 text-muted-foreground" /> : <EyeOff className="h-3 w-3 text-muted-foreground" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
